document.addEventListener('alpine:init', () => {
    Alpine.data('digestiveSystem', () => ({
        active: 0,
        stageHeight: null,
        stageAnimated: false,
        touchStartX: null,
        touchStartY: null,
        stages: [
            {
                name: 'Mouth',
                x: 9.1,
                y: 19,
                blurb: 'Chewing reduces feed particle size and triggers saliva production. Bicarbonate-rich saliva helps buffer stomach acid, making increased chewing time with forage a key factor in digestive health.'
            },
            {
                name: 'Esophagus',
                x: 25.8,
                y: 33.4,
                blurb: 'Moves food from mouth to stomach using wave-like muscle contractions (peristalsis). Food can only travel in one direction and cannot be regurgitated, making slow, thorough chewing essential.'
            },
            {
                name: 'Stomach',
                x: 47.5,
                y: 50.3,
                blurb: 'The horse’s stomach holds 2-4 gallons and secretes acid continuously. Frequent forage access helps buffer stomach acid and keeps the stomach environment healthy.'
            },
            {
                name: 'Small Intestine',
                x: 51.8,
                y: 43.5,
                blurb: 'At roughly 70 feet long, this is where starches, sugars, amino acids, fats, and most vitamins and minerals are absorbed. Overloading it by feeding large grain meals can push undigested starch into the hindgut and possibly increase the risk of digestive disturbance.'
            },
            {
                name: 'Cecum',
                x: 60,
                y: 44,
                blurb: 'With a 7-8 gallon capacity, the cecum is where fermentation begins. Billions of microbes start breaking down fiber, producing volatile fatty acids (VFAs) that can be used as energy. Sudden dietary changes can disrupt this sensitive microbial population.'
            },
            {
                name: 'Large Colon',
                x: 62.7,
                y: 50.3,
                blurb: 'With 10-14 gallon capacity, fermentation continues in the large colon as microbes further break down fiber and volatile fatty acids (VFAs) are absorbed for energy. Some additional mineral absorption occurs here, secondary to the small intestine, and water is reabsorbed back into the body.'
            },
            {
                name: 'Small Colon',
                x: 67.8,
                y: 43,
                blurb: 'The small colon reabsorbs remaining water and is where fecal balls are formed. Manure consistency is a direct reflection of how well this process is functioning.'
            },
            {
                name: 'Rectum',
                x: 80,
                y: 39.5,
                blurb: 'Temporarily stores formed fecal balls before elimination, completing a digestive process that takes 36-72 hours to finish.'
            },
        ],
        init() {
            this.$nextTick(() => this.measureStage());
            window.addEventListener('resize', () => this.measureStage());
        },
        select(index) {
            this.active = index;
            this.$nextTick(() => this.measureStage());
        },
        measureStage() {
            const track = this.$refs.track;
            if (!track) return;
            const panel = track.querySelectorAll('.forage-foundation-digestive-panel')[this.active];
            if (panel && panel.offsetHeight > 0) {
                this.stageHeight = panel.offsetHeight;
                if (!this.stageAnimated) this.$nextTick(() => { this.stageAnimated = true; });
            }
        },
        prev() {
            if (this.active > 0) this.select(this.active - 1);
        },
        next() {
            if (this.active < this.stages.length - 1) this.select(this.active + 1);
        },
        onTouchStart(event) {
            const touch = event.changedTouches[0];
            this.touchStartX = touch.clientX;
            this.touchStartY = touch.clientY;
        },
        onTouchEnd(event) {
            if (this.touchStartX === null) return;
            const touch = event.changedTouches[0];
            const deltaX = touch.clientX - this.touchStartX;
            const deltaY = touch.clientY - this.touchStartY;
            this.touchStartX = null;
            this.touchStartY = null;
            if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
            if (deltaX < 0) this.next();
            else this.prev();
        },
        stageLabel(index) {
            return 'Stage ' + String(index + 1).padStart(2, '0');
        },
        get navProgress() {
            return `--forage-foundation-digestive-inset: ${50 / this.stages.length}%; --forage-foundation-digestive-progress: ${this.active / this.stages.length * 100}%`;
        },
        hotspot(index) {
            return {
                type: 'button',
                [':aria-label']() { return this.stages[index].name; },
                [':aria-pressed']() { return this.active === index; },
                [':class']() { return this.active === index && 'is-active'; },
                [':style']() { return `left: ${this.stages[index].x}%; top: ${this.stages[index].y}%`; },
                ['@click']() {
                    this.select(index);
                    this.$refs.nav.scrollIntoView({ behavior: 'smooth', block: 'start' });
                },
            };
        },
        navItem(index) {
            return {
                type: 'button',
                [':aria-pressed']() { return this.active === index; },
                [':class']() { return { 'is-active': this.active === index, 'is-done': index < this.active }; },
                ['@click']() { this.select(index); },
            };
        },
    }));

    Alpine.data('forageDietChart', () => ({
        open: false,
        active: 0,
        dots: [
            {
                x: 88,
                y: 50,
                section: 0
            },
            {
                x: 26.4,
                y: 25.6,
                section: 1
            },
            {
                x: 15.1,
                y: 44.8,
                section: 2
            },
            {
                x: 14.3,
                y: 55.5,
                section: 3
            },
        ],
        sections: [
            {
                title: 'Forage',
                content: `<p>The foundation of every horse’s diet, forage should make up the largest portion of the ration at a rate of 1.5-2.5% of body weight per day.</p>
                <p>Sources include fresh pasture, dried long-stem hay, and products such hay pellets, cubes, and chopped forage (chaff).</p>
                <p>Consistent fiber intake helps maintain digestive health and fuels hindgut fermentation for steady energy throughout the day.</p>
                <p><b>THE FOUNDATION OF A HORSE’S DIET STARTS WITH FORAGE.</b></p>`
            },
            {
                title: 'Concentrates',
                content: `<p>Concentrates, such as straight grains and commercially available pelleted or textured feeds, are more concentrated in calories and/or nutrients than forage. These can be used to help provide extra calories if needed, but should complement a strong forage foundation, not replace it.</p>
                <p><i>Concentrates are best fed in smaller, more frequent meals.</i></p>`
            },
            {
                title: 'Fat',
                content: `<p>Fat sources such as vegetable oil and rice bran can also boost energy intake.</p>`
            },
            {
                title: 'Supplements',
                content: `<p>Supplements, like <b>vitamins, minerals, amino acids,</b> and other targeted ingredients are used to fill nutritional gaps when forage alone does not meet essential nutrient requirements.</p>
                <p>Because most forages are naturally low in <b>sodium</b>, horses should always have access to a salt source.</p>
                <p><i>A <a href="https://dairylandlabs.com/" target="_blank" title="Forage testing and analysis">forage analysis</a> is a good starting point for identifying nutritional gaps and determining what supplements a horse needs.</i></p>`
            },
        ],
        get current() {
            return this.sections[this.active];
        },
        openSection(index) {
            this.active = index;
            this.open = true;
            document.body.classList.add('modal-overlay-overflow');
        },
        closeModal() {
            this.open = false;
            document.body.classList.remove('modal-overlay-overflow');
        },
        dotButton(index) {
            return {
                type: 'button',
                [':style']() { return `left: ${this.dots[index].x}%; top: ${this.dots[index].y}%`; },
                [':aria-label']() { return this.sections[this.dots[index].section].title; },
                ['@click']() { this.openSection(this.dots[index].section); },
            };
        },
    }));
});
